import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormElements } from "../../../shared/ui/forms/FormElements";
import HeadingH4 from "../../../shared/ui/headings/headingH4/HeadingH4";
import {
    formStructureMedicoRow,
    formStructurePrescrizioneRow,
    getValidationsTemplateGenPub
} from "./templateGenPubData";

import SecondaryButton from "../../../shared/ui/buttons/secondary-button/SecondaryButton";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
    selectIsPharmacy,
    selectSelectedCustomer
} from "../../../../redux/customer/customerSlice";
import { selectUser } from "../../../modules/Auth/_redux/authRedux";
import { toEnDateString } from "../../../utils/dateUtils";
import BtnPrimary from "../../../../shared/bootstrap/button/primary/BtnPrimary";
import BtnSecondary from "../../../../shared/bootstrap/button/secondary/BtnSecondary";
import { useLanguageForShared } from "../../../../shared/utils/languages/useLanguageForShared";
import BoxSearchPatients from "../templateGenFar/component/BoxSearchPatients";
import {
    formStructureContattiRow,
    formStructureIndirizzoConsegnaRow, formStructureIndirizzoResidenceRow,
    formStructureNominativeRow
} from "../templateGenFar/templateGenFarData";
import Checkbox from "../../../../shared/bootstrap/form/checkbox/Checkbox";
import { IconSVG } from "../../../../shared/custom/icon/IconSVG";
import configuration from "../../../../shared/configuration";
import {
    formStructureEditPatientRegistryRow, formStructureEditPatientRegistryRowDomicile,
    formStructureEditPatientRegistryRowResidence
} from "../anagraficaPaziente/patientRegistryData";
import { useIsUpdatePatient } from "../useIsUpdatePatient";
import { updatePatient } from "../../../../redux/order/orderCrud";
import { setErrorTypeAction } from "../../../../redux/errors/errorsActions";
import HeadingH3 from "../../../shared/ui/headings/headingH3/HeadingH3";
import {
    getCustomerDistrictOptions,
    hasCustomerDistricts,
    isCustomerDistrictRequired,
    isDistrictGuidAllowed,
    sanitizePatientDistrictPayload
} from "../../../utils/districtUtils";

// Helper to check if any residence/domicile field differs
function isDomicileDifferent(residence, domicile) {
    if (!residence || !domicile) return false;
    return Object.keys(residence).some(
        key => key !== "districtGuid" && residence[key] !== domicile[key]
    );
}

function getSyncedDomicile(residence = {}, domicile = {}) {
    return {
        ...domicile,
        street: residence?.street,
        number: residence?.number,
        internal: residence?.internal,
        cap: residence?.cap,
        city: residence?.city,
        province: residence?.province,
    };
}

const TemplateGenPub = ({ createOrder, order, goBack, patient }) => {
    const { t } = useLanguageForShared();

    const isPharmacy = useSelector(selectIsPharmacy);
    const selectedCustomer = useSelector(selectSelectedCustomer);

    const user = useSelector(selectUser);

    const dispatch = useDispatch();
    const districtOptions = getCustomerDistrictOptions(selectedCustomer);
    const validationsTemplateGenPub = useMemo(
        () => getValidationsTemplateGenPub(selectedCustomer),
        [selectedCustomer]
    );

    const shouldCopyDomicile = useRef(false);

    const initialIsDomicileDifferent = isDomicileDifferent(
        patient?.residence,
        patient?.domicile
    );

    const formik = useFormik({
        initialValues: {
            isDomicileDifferent: initialIsDomicileDifferent,
            prescription: {
                date: toEnDateString(new Date().getTime())
            },
            prescriber: {
                name: user.firstname,
                surname: user.lastname
            },
            patient: {
                ...patient
            }
        },
        validationSchema: validationsTemplateGenPub,
        onSubmit: (values) => {
            values = {
                ...values,
                patient: {
                    ...sanitizePatientDistrictPayload(selectedCustomer, values?.patient)
                }
            };

            const districtGuid = values?.patient?.residence?.districtGuid;
            if (isCustomerDistrictRequired(selectedCustomer) && !districtGuid) {
                dispatch(setErrorTypeAction("Il distretto di residenza è obbligatorio"));
                return;
            }

            if (hasCustomerDistricts(selectedCustomer) && !isDistrictGuidAllowed(selectedCustomer, districtGuid)) {
                dispatch(setErrorTypeAction("Distretto di residenza non valido per il cliente selezionato"));
                return;
            }

            if (values.isDomicileDifferent === false) {
                const residence = values.patient?.residence;
                const domicile = values.patient?.domicile;

                if (residence && domicile) {
                    Object.keys(residence).forEach((key) => {
                        if (key === "districtGuid") {
                            return;
                        }
                        const residenceValue = residence[key];

                        if (
                            residenceValue !== undefined &&
                            residenceValue !== null &&
                            residenceValue !== "" &&
                            domicile[key] !== residenceValue
                        ) {
                            formik.setFieldValue(`patient.domicile.${key}`, residenceValue, false);
                            values = { ...values, patient: { ...values.patient, domicile: { ...values.patient.domicile, [key]: residenceValue } } };
                        }
                    });
                }
            }

            const applicant = {
                name: user.firstname,
                surname: user.lastname,
                data: toEnDateString(new Date().getTime())
            };

            const callback = (data = {}) => {
                createOrder({ ...values, applicant, ...data });
            };

            updateRegistry ? updatePatientAction(callback) : callback();
        }
    });

    const resetDomicileValidationState = React.useCallback(() => {
        formik.setTouched((prevTouched) => ({
            ...prevTouched,
            patient: {
                ...prevTouched?.patient,
                domicile: undefined,
            },
        }), false);
        formik.setErrors((prevErrors) => ({
            ...prevErrors,
            patient: {
                ...prevErrors?.patient,
                domicile: undefined,
            },
        }));
        formik.validateForm();
    }, [formik]);

    const updatePatientAction = (callback) => {
        updatePatient(sanitizePatientDistrictPayload(selectedCustomer, formik.values.patient)).then(
            (res) => {
                callback && callback({ patient: res });
            },
            (err) => {
                const message = err?.response?.data?.Message || err?.response?.data?.message;
                if (message === "ERR_CF_DUPLICATE") {
                    dispatch(setErrorTypeAction(t("sg.msg.cfAlreadyRegistered")));
                } else if (message === "ERR_CF_INCOHERENT") {
                    dispatch(setErrorTypeAction(t("sg.msg.cfWrong")));
                } else if (message === "ERR_EMAIL_DUPLICATE") {
                    dispatch(setErrorTypeAction(t("sg.msg.emailAlreadyRegistered")));
                } else if (message === "ERR_EMAIL_INCOHERENT") {
                    dispatch(setErrorTypeAction(t("sg.msg.emailWrong")));
                } else if (message === "ERR_DISTRICT_CUSTOMER_REQUIRED") {
                    dispatch(setErrorTypeAction("Se selezioni un distretto devi indicare il cliente"));
                } else if (message === "ERR_DISTRICT_CUSTOMER_INVALID") {
                    dispatch(setErrorTypeAction("Il distretto selezionato non appartiene al cliente"));
                } else {
                    dispatch(
                        setErrorTypeAction(t("sg.msg.errorUpdatePatient"))
                    );
                }
            }
        );
    };

    useEffect(() => {
        order && formik.setValues({
            ...order,
            prescription: order.prescription || {
                date: toEnDateString(new Date().getTime())
            },
            prescriber: order.prescriber || {
                name: user.firstname,
                surname: user.lastname
            }
        });
    }, [order]);

    useEffect(() => {
        (!order?.patient) && formik.setFieldValue("patient", patient);
    }, [patient]);



    // Sincronizza solo il campo 'street'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.patient?.residence?.street !== undefined &&
            formik.values.patient?.domicile
        ) {
            const val = formik.values.patient.residence.street;
            if (formik.values.patient.domicile.street !== val) {
                formik.setFieldValue("patient.domicile.street", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.patient?.residence?.street]);

    // Sincronizza solo il campo 'number'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.patient?.residence?.number !== undefined &&
            formik.values.patient?.domicile
        ) {
            const val = formik.values.patient.residence.number;
            if (formik.values.patient.domicile.number !== val) {
                formik.setFieldValue("patient.domicile.number", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.patient?.residence?.number]);

    // Sincronizza solo il campo 'internal'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.patient?.residence?.internal !== undefined &&
            formik.values.patient?.domicile
        ) {
            const val = formik.values.patient.residence.internal;
            if (formik.values.patient.domicile.internal !== val) {
                formik.setFieldValue("patient.domicile.internal", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.patient?.residence?.internal]);

    // Sincronizza solo il campo 'cap'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.patient?.residence?.cap !== undefined &&
            formik.values.patient?.domicile
        ) {
            const val = formik.values.patient.residence.cap;
            if (formik.values.patient.domicile.cap !== val) {
                formik.setFieldValue("patient.domicile.cap", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.patient?.residence?.cap]);

    // Sincronizza solo il campo 'city'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.patient?.residence?.city !== undefined &&
            formik.values.patient?.domicile
        ) {
            const val = formik.values.patient.residence.city;
            if (formik.values.patient.domicile.city !== val) {
                formik.setFieldValue("patient.domicile.city", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.patient?.residence?.city]);

    // Sincronizza solo il campo 'province'
    useEffect(() => {
        if (
            formik.values.isDomicileDifferent === false &&
            formik.values.patient?.residence?.province !== undefined &&
            formik.values.patient?.domicile
        ) {
            const val = formik.values.patient.residence.province;
            if (formik.values.patient.domicile.province !== val) {
                formik.setFieldValue("patient.domicile.province", val, false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.patient?.residence?.province]);

    useEffect(() => {
        if (
            shouldCopyDomicile.current &&
            formik.values.isDomicileDifferent === false
        ) {
            formik.setFieldValue(
                "patient.domicile",
                getSyncedDomicile(formik.values.patient?.residence, formik.values.patient?.domicile),
                false
            );
            shouldCopyDomicile.current = false;
            resetDomicileValidationState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.isDomicileDifferent, resetDomicileValidationState]);


    const { updateRegistry, setUpdateRegistry, isUpdatePatient } = useIsUpdatePatient(patient, formik.values.patient);

    return (
        <div className={"templates-wrap template-gen-pub"}>
            <Form autocomplete="off" onSubmit={formik.handleSubmit}>
                {/*<HeadingH4>Richiedente</HeadingH4>*/}
                {/*<FormElements*/}
                {/*	structure={formStructureRichiedenteRow}*/}
                {/*	formik={formik}></FormElements>*/}
                <HeadingH3 className={"mb-3"}>{t("sg.infoPrescription")}</HeadingH3>

                <HeadingH4>{t("sg.order.prescriber.prescriberOrder")}</HeadingH4>

                <FormElements
                    structure={formStructureMedicoRow(t)}
                    formik={formik}></FormElements>
                <div className={"mt-3"}>
                    <HeadingH4>{t("sg.order.prescriber.prescriptionRight")}</HeadingH4>
                    <FormElements
                        structure={formStructurePrescrizioneRow(t)}
                        formik={formik}></FormElements>
                </div>

                {/*	<HeadingH4>Diagnosi</HeadingH4>
				<FormElements
					structure={formStructureDiagnosiRow}
					formik={formik}></FormElements>*/}
                <hr />
                <HeadingH3 className={"mt-3 mb-3"}>{t("sg.infoPatientPrescription")}</HeadingH3>

                <HeadingH4>{t("sg.patientLabel")}</HeadingH4>
                <FormElements
                    structure={
                        formStructureNominativeRow(t)
                    }
                    formik={formik}
                ></FormElements>

                <HeadingH4 className={"mt-3"}>{t("sg.contacts")}</HeadingH4>
                <FormElements
                    structure={formStructureContattiRow(t)}
                    formik={formik}
                ></FormElements>

                <HeadingH4 className={"mt-3"}>{t("sg.residenceAddress")}</HeadingH4>
                <FormElements
                    structure={formStructureIndirizzoResidenceRow(t, districtOptions)}
                    formik={formik}
                />

                <div className="mb-3" id="isDomicileDifferent">
                    <Form.Check
                        type="checkbox"
                        label={t('sg.patient.differentDomicile')}
                        name="isDomicileDifferent"
                        checked={formik.values.isDomicileDifferent}
                        onChange={e => {
                            const checked = e.target.checked;
                            if (!checked) {
                                shouldCopyDomicile.current = true;
                            }
                            formik.setFieldValue("isDomicileDifferent", checked, false);
                            if (!checked) {
                                resetDomicileValidationState();
                            }
                        }}
                    />
                </div>

                <HeadingH4 className={"mt-3"}>{t("sg.homeAddress")}</HeadingH4>
                <FormElements
                    structure={formStructureIndirizzoConsegnaRow(t)}
                    formik={formik}
                    disabled={!formik.values.isDomicileDifferent}
                />

                {isUpdatePatient && <div className={"update-registry-check py-5 mb-5"}>
                    <Checkbox name="updateRegistry"
                        value={updateRegistry}
                        label={t("sg.updateRegistry")}
                        onChange={() => setUpdateRegistry(!updateRegistry)}
                        checked={updateRegistry} />
                </div>}

                <Row className="justify-content-end" style={{ marginTop: "32px" }}>
                    <Col xs="auto">
                        <BtnSecondary onClick={goBack}>{isPharmacy ? t("action.cancel")
                            : t("action.back")}</BtnSecondary>
                    </Col>
                    <Col xs="auto">
                        <BtnPrimary type="submit">{t("action.next")}</BtnPrimary>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default TemplateGenPub;
